// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type MultiAssetPortfolioBacktest,
  type PortfolioBacktest,
  type PortfolioValidationResp,
} from '@shared/data-access/interfaces/api/ai/portfolio';
import {
  portfolioStrategyPayload,
  type PortfolioStrategy,
} from '@shared/data-access/interfaces/api/ai/portfolio-strategy';
import {
  type MultiAssetPortfolioAnalysisRequestBody,
  type BaseMultiAssetPortfolioAnalysis,
  type PortfolioAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import {
  type QueryReturnValue,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { cleanPayload } from '@shared/data-access/utils/clean-payload';
import { aiApi } from '@shared/data-access/api-base/citec-ai-api';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const portfolioMultiAssetApi = aiApi.injectEndpoints({
  endpoints: build => ({
    portfolioValidationMultiAsset: build.query<
      PortfolioValidationResp,
      Omit<MultiAssetPortfolioAnalysisRequestBody, 'params'>
    >({
      query: ({ payload }) => ({
        url: '/portfolio/multiasset/validation/',
        method: 'POST',
        body: cleanPayload(payload, portfolioStrategyPayload),
      }),
      providesTags: [{ type: 'PortfolioMultiAsset', id: 'Validation' }],
    }),

    portfolioMultiAssetAnalysis: build.query<
      BaseMultiAssetPortfolioAnalysis,
      MultiAssetPortfolioAnalysisRequestBody
    >({
      queryFn: async (
        { params, payload },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const validationResponse = (await fetchWithBQ({
          url: '/portfolio/multiasset/validation/',
          method: 'POST',
          body: cleanPayload(payload, portfolioStrategyPayload),
        })) as QueryReturnValue<
          PortfolioValidationResp,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (validationResponse.error)
          return { error: validationResponse.error as FetchBaseQueryError };

        const benchmark = payload.benchmark.target_asset.filter(
          asset =>
            asset.ticker === '$CASH' ||
            !validationResponse.data.unsupported_benchmarks.includes(asset.ticker),
        );

        const totalBenchmarkWeight = benchmark.reduce(
          (acc, asset) => acc + asset.weight,
          0,
        );

        const analysisPayload: PortfolioStrategy = {
          benchmark: {
            target_asset: benchmark.map(asset => ({
              ticker: asset.ticker,
              weight: asset.weight / totalBenchmarkWeight,
            })),
          },
          holdings: payload.holdings.filter(holding => {
            const ticker = holding.ticker;

            return !validationResponse.data.unsupported_ticker_positions.includes(
              ticker,
            );
          }),
        };

        const analysisResponse = (await fetchWithBQ({
          url: '/portfolio/multiasset/analysis/',
          method: 'POST',
          params,
          body: cleanPayload(analysisPayload, portfolioStrategyPayload),
        })) as QueryReturnValue<
          PortfolioAnalysis,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (analysisResponse.error)
          return { error: analysisResponse.error as FetchBaseQueryError };

        return {
          data: {
            ...analysisResponse.data,
            unsupported_benchmarks: validationResponse.data.unsupported_benchmarks,
            unsupported_ticker_positions:
              validationResponse.data.unsupported_ticker_positions,
          },
        };
      },
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: [
        { type: 'PortfolioMultiAsset', id: 'Analysis' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    portfolioMultiAssetBacktest: build.query<
      MultiAssetPortfolioBacktest,
      MultiAssetPortfolioAnalysisRequestBody
    >({
      queryFn: async (
        { params, payload },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) => {
        const validationResponse = (await fetchWithBQ({
          url: '/portfolio/multiasset/validation/',
          method: 'POST',
          body: cleanPayload(payload, portfolioStrategyPayload),
        })) as QueryReturnValue<
          PortfolioValidationResp,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (validationResponse.error)
          return { error: validationResponse.error as FetchBaseQueryError };

        const benchmark = payload.benchmark.target_asset.filter(
          asset =>
            asset.ticker === '$CASH' ||
            !validationResponse.data.unsupported_benchmarks.includes(asset.ticker),
        );

        const totalBenchmarkWeight = benchmark.reduce(
          (acc, asset) => acc + asset.weight,
          0,
        );

        const backtestPayload: PortfolioStrategy = {
          benchmark: {
            target_asset: benchmark.map(asset => ({
              ticker: asset.ticker,
              weight: asset.weight / totalBenchmarkWeight,
            })),
          },
          holdings: payload.holdings.filter(holding => {
            const ticker = holding.ticker;

            return !validationResponse.data.unsupported_ticker_positions.includes(
              ticker,
            );
          }),
        };

        const backtestResponse = (await fetchWithBQ({
          url: '/portfolio/multiasset/backtest/',
          method: 'POST',
          params,
          body: cleanPayload(backtestPayload, portfolioStrategyPayload),
        })) as QueryReturnValue<
          PortfolioBacktest,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (backtestResponse.error)
          return { error: backtestResponse.error as FetchBaseQueryError };

        return {
          data: {
            ...backtestResponse.data,
            unsupported_benchmarks: validationResponse.data.unsupported_benchmarks,
            unsupported_ticker_positions:
              validationResponse.data.unsupported_ticker_positions,
          },
        };
      },
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: [
        { type: 'PortfolioMultiAsset', id: 'Backtest' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),
  }),
});
